import { useTheme } from "@emotion/react";
import { Download, Print, StarOutlineOutlined } from "@mui/icons-material";
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { Box, ButtonBase, Checkbox } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ButtonGroup from "../../../../components/theme/buttons/ButtonGroup";
import Shbutton from "../../../../components/theme/buttons/Shbutton";
import { DropdownBase, DropdownBaseV2 } from "../../../../components/theme/dropdowns/Dropdown";
import ShLabel from "../../../../components/theme/label/ShLabel";
import Text from "../../../../components/theme/text/Text";
import { tokens } from "../../../../theme";
import { formatNumber } from "../../../../utils/helpers";
import { getVirtualProductNames } from "../helper";
import SHQuickAction from "../../../../components/theme/Modal/SHQuickAction";
import { ProductHoverDetails } from "../orders/OrdersEcheckView2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Subheading from "../../../../components/theme/text/Subheading";
import DeleteIcon from '@mui/icons-material/Delete';
import UndoIcon from '@mui/icons-material/Undo';
import cx from 'classnames';
import { useDraggable, useDroppable } from "@dnd-kit/core";
import { orderline_type } from "../../../../utils/staticEnums";
import {  useGetShipmentLabelMutationMutation, usePrintShipmentLabelMutation } from "../../../../newapi/order/ordersSlice";
import MSelectLabel from "../../../../components/global/Modals/MSelectLabel";
import TurnedInIcon from '@mui/icons-material/TurnedIn';
import { useGetPickingSlipMutation } from "../../../../newapi/order/shipmentsSlice";
import MDynamicValues from "../../../../components/global/Modals/MDynamicValues";
import MEcheckCarrierShipper from "../../../../components/global/Modals/MEcheckCarrierShipper";
import MEcheckUndoAmount from "../../../../components/global/Modals/MEcheckUndoAmount";
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { getShipperColorSettingsByType } from "../../../../components/Streams/StreamShipperSettings";
import { showToastMessage } from "../../../../utils/toasts";
import { booleanValueTypes } from "../../../../components/Streams/Conditions/ConditionSettings";
const CustomComponent = (props) => {
    return (
    <Box sx={{display:"flex", justifyContent:"flex-start", alignItems:"flex-start", gap:1}}>
        {props.option.labelDisplayName}
        (€{props.option.labelPrice.totalPrice})
        {props.option.recommended ? <StarOutlineOutlined width={16} height={16}/> : <></>}
    </Box>
    );
};


const toBoolean = value => {
    if (value === true || value === false) return value;      // already boolean
    if (value === "true") return true;
    if (value === "false") return false;
    return Boolean(value); // fallback for other cases
};

export const DraggedRowPreview = ({ dragId, carriers }) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const colors = tokens(theme.palette.mode); 
    // Parse the dragId string like "bundle-0-carrier-1-row-3" or "carrier-2-row-4"
    const parseDragId = (id) => {
        const bundleMatch = id.match(/^bundle-(\d+)-carrier-(\d+)-row-(\d+)$/);
        if (bundleMatch) {
            return {
                bundleIndex: parseInt(bundleMatch[1], 10),
                carrierIndex: parseInt(bundleMatch[2], 10),
                rowIndex: parseInt(bundleMatch[3], 10),
            };
        }
        const carrierMatch = id.match(/^carrier-(\d+)-row-(\d+)$/);
        if (carrierMatch) {
            return {
                bundleIndex: null,
                carrierIndex: parseInt(carrierMatch[1], 10),
                rowIndex: parseInt(carrierMatch[2], 10),
            };
        }
        return null;
    };

    const pos = parseDragId(dragId);
    if (!pos) return null;

    const carrier = pos.bundleIndex !== null ? carriers[pos.bundleIndex].carriers[pos.carrierIndex] : carriers[pos.carrierIndex];
    if (!carrier) return null;

    const row = carrier.rows[pos.rowIndex];
    if (!row) return null;

    return (
        <Box
            className={cx('', {
                'is-addon' : row.type === orderline_type.ADD_ON,
                'is-serialnumber': row.serialnumbers?.length > 0,
            })}
            sx={{
                flex:1,
                display:"flex",
                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
                borderRadius:3,
                padding:1,
                alignItems:"center",
                justifyContent: "space-between",
                animation: "1s pulsate",
                "&.is-serialnumber" : {
                    background:colors.pastel.light.lightblue[100],
                    border: `1px solid ${colors.pastel.light.lightblue[200]}`
                },
                "&.is-addon": {
                    background:colors.pastel.light.orange[100],
                    border: `1px solid ${colors.pastel.light.orange[200]}`
                }
            }}
        >
            <Box
                sx={{
                    display:"flex",
                    alignItems:"center",
                    width:1,
                }}
            >
                <ShLabel size={32} fitted palette="normal" variant={"orange"}>
                    1
                </ShLabel>
                
                <Box
                    sx={{
                        ml:2,
                        display:"flex",
                        flexDirection:'column',
                    }}
                >
                    <Text>
                        {row.product.barcodes?.[0].barcode}
                    </Text>
                    <Text semibold>
                        {row.product.description}
                    </Text>
                    {getVirtualProductNames(row).map(name => (
                        <ShLabel key={"vp"+name} size={24} fitted variant="purple" sx={{mt:0.5, alignSelf:"flex-start", height:"auto", minHeight:24, lineHeight:"18px", py:"2px", textAlign:"left"}}>
                            {name}
                        </ShLabel>
                    ))}
                </Box>
                {row.serialnumbers?.length >0 &&
                    <Box
                        sx={{
                            height:32,
                            ml:"auto",
                            px:1.5,
                            mr:1,
                            background:colors.pastel.light.lightblue[600],
                            color:colors.pastel.light.lightblue[100],
                            borderRadius:2,
                            lineHeight:"32px",
                            fontWeight:500,
                            fontFamily:"Roboto, sans-serif",
                            fontSize:14,
                            my:"auto",
                        }}
                    >
                        {row.serialnumbers[0].barcode}
                    </Box>
                }
                {row.type === orderline_type.ADD_ON &&
                    <Box
                        sx={{
                            height:32,
                            ml:"auto",
                            px:1.5,
                            mr:1,
                            background:colors.pastel.light.orange[600],
                            color:colors.pastel.light.orange[100],
                            borderRadius:2,
                            lineHeight:"32px",
                            fontWeight:500,
                            fontFamily:"Roboto, sans-serif",
                            fontSize:14,
                            my:"auto",
                        }}
                    >
                        {t("add_on")}
                    </Box>
                }
            </Box>

        </Box>
    );
};

const CarrierRow = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: props.dragId,
    });

    const style = {
        opacity: isDragging ? 0.5 : 1,
        height: (isDragging && (props.row.amount - 1 === 0)) ? "0px" : null,
        overflow: (isDragging && (props.row.amount - 1 === 0)) ? "hidden" : null,
        cursor: 'grab',
    };


    return (
        <div  
            ref={setNodeRef} 
            {...listeners} 
            {...attributes} 
            style={style}
        >
            <SHQuickAction
                key={"scan-prod"+props.index}
                component={<ProductHoverDetails productId={props.row.product.id}  product={props.row.product}/>}
                
                disableAutoFocus
                disableEnforceFocus
                sx={{
                    modal:{
                        body:{
                            ml:2,
                        }
                    }
                }}
            >
                {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                    <Box
                        className={cx('', {
                            'is-addon' : props.row.type === orderline_type.ADD_ON,
                            'is-serialnumber': props.row.serialnumbers?.length > 0,
                        })}
                        aria-owns={anchorEl ? 'simple-popover' : undefined}
                        aria-haspopup="true"
                        onMouseEnter={onMouseEnter}
                        onMouseLeave={onMouseLeave}
                        onTouchStart={onTouchStart} 
                        sx={{
                            flex:1,
                            display:"flex",
                            background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                            border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                            boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
                            borderRadius:3,
                            padding:1,
                            alignItems:"center",
                            justifyContent: "space-between",
                            flexDirection:"column",
                            animation: "1s pulsate",
                            "&.is-serialnumber" : {
                                background:colors.pastel.light.lightblue[100],
                                border: `1px solid ${colors.pastel.light.lightblue[200]}`
                            },
                            "&.is-addon": {
                                background:colors.pastel.light.orange[100],
                                border: `1px solid ${colors.pastel.light.orange[200]}`
                            }
                        }}
                    >
                        <Box
                            sx={{
                                display:"flex",
                                alignItems:"center",
                                width:1,
        
                            }}
                        >
                            <Box
                                sx={{
                                    height:32,
                                    px:1.5,
                                    background:props.row.type !== orderline_type.ADD_ON ? colors.orange['100'] : colors.pastel.st.orange[200],
                                    color:props.row.type !== orderline_type.ADD_ON ? colors.orange['400'] : colors.pastel.light.orange[600],
                                    border: `1px solid ${props.row.type !== orderline_type.ADD_ON ? colors.orange[400] : colors.pastel.light.orange[600]}`,
                                    // color:colors.pastel.light.lightblue[100],
                                    borderRadius:4,
                                    lineHeight:"32px",
                                    fontWeight:500,
                                    fontFamily:"Roboto, sans-serif",
                                    fontSize:14,
                                }}
                            >
                                {isDragging ? props.row.amount -1 : props.row.amount}

                            </Box>
                            {/* <ShLabel size={32} fitted palette="normal" variant={"orange"}>
                                {isDragging ? props.row.amount -1 : props.row.amount}
                            </ShLabel> */}
                            
                            <Box
                                sx={{
                                    ml:2,
                                    display:"flex",
                                    flexDirection:'column',
                                }}
                            >
                                <Text

                                    sx={{
                                        whiteSpace:"pre",
                                        color:props.row.type !== orderline_type.ADD_ON ? 
                                            colors.txt["primary"] 
                                            : theme.palette.mode === "dark" 
                                                ? colors.txt["primaryDark"] 
                                                : colors.txt['primary']
                                    }} 
                                >
                                    {props.row.product.barcodes?.[0].barcode}
                                </Text>
                                <Text semibold
                                    
                                    sx={{

                                        color:props.row.type !== orderline_type.ADD_ON ? colors.txt["primary"] : theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['primary']
                                    }}
                                >
                                    {props.row.product.description}
                                </Text>
                                {getVirtualProductNames(props.row).map(name => (
                                    <ShLabel key={"vp"+name} size={24} fitted variant="purple" sx={{mt:0.5, alignSelf:"flex-start", height:"auto", minHeight:24, lineHeight:"18px", py:"2px", textAlign:"left"}}>
                                        {name}
                                    </ShLabel>
                                ))}
                            </Box>
                            <Box
                                sx={{
                                    ml:"auto",
                                    display:"flex",
                                    gap:1,
                                }}
                            >
                                {props.row.serialnumbers?.length >0 &&
                                    <Box
                                        sx={{
                                            height:32,
                                            px:1.5,
                                            mr:1,
                                            background:colors.pastel.light.lightblue[600],
                                            color:colors.pastel.light.lightblue[100],
                                            borderRadius:2,
                                            lineHeight:"32px",
                                            fontWeight:500,
                                            fontFamily:"Roboto, sans-serif",
                                            fontSize:14,
                                            my:"auto",
                                        }}
                                    >
                                        {props.row.serialnumbers[0].barcode}
                                    </Box>
                                }
                                {props.row.type === orderline_type.ADD_ON &&
                                    <Box
                                        sx={{
                                            height:32,
                                            px:1.5,
                                            mr:1,
                                            background:colors.pastel.light.orange[600],
                                            color:colors.pastel.light.orange[100],
                                            borderRadius:2,
                                            lineHeight:"32px",
                                            fontWeight:500,
                                            fontFamily:"Roboto, sans-serif",
                                            fontSize:14,
                                            my:"auto",
                                        }}
                                    >
                                        {t("add_on")}
                                    </Box>
                                }
                                {props.onUndoClick &&
                                    <ButtonBase
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            props.onUndoClick(props.row, props.carrierIndex, props.bundleIndex);
                                        }}
                                        sx={{
                                            ml:"auto",
                                            minWidth:32,
                                            height:32,
                                            borderRadius:2,
                                            display:"flex",
                                            alignItems:"center",
                                            justifyContent:"center",
                                            color:colors.red[400],
                                            "&:hover":{
                                                background:colors.red[100],
                                            }
                                        }}
                                    >
                                        <UndoIcon sx={{fontSize:18}}/>
                                    </ButtonBase>
                                }
                            </Box>
                        </Box>

                        {props.row.product?.productFieldValues?.some(
                            pfv => pfv.value !== null && pfv.value !== "" && pfv.value !== undefined &&
                                (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                        ) &&
                            <Box
                                sx={{
                                    width:1,
                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                                    display:'flex',
                                    flexDirection:"column",
                                    gap:1,
                                    px:1.5,
                                    py:1.5,
                                    borderRadius:2,
                                    mt:1.5,
                                }}
                            >
                                {props.row.product.productFieldValues
                                    .filter(pfv =>
                                        pfv.value !== null &&
                                        pfv.value !== "" &&
                                        pfv.value !== undefined &&
                                        (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                                    )
                                    .map((pfv, pfvidex) => {
                                        return (
                                            booleanValueTypes.includes(pfv.productfield.valueType) ?
                                            <Box
                                                key={"pfv"  + pfvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${toBoolean(pfv.value) ? colors.green[400] : colors.red[400]}`,
                                                    background: toBoolean(pfv.value) ? colors.green[100] : colors.red[100],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text>
                                                    {pfv.productfield?.name}
                                                </Text>

                                                <Box
                                                    sx={{
                                                        color: toBoolean(pfv.value) ? colors.green[400] : colors.red[400],
                                                        fontSize:14,
                                                        fontWeight:700,
                                                        fontFamily:"Roboto,sans-serif",
                                                        lineHeight:"16px",
                                                    }}
                                                >
                                                    {t(toBoolean(pfv.value) ? "yes" : 'no')}
                                                </Box>
                                            </Box>
                                            :
                                            <Box
                                                key={"pfv" + pfvidex}
                                                sx={{
                                                    height:24,
                                                    border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                    background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                    borderRadius:3,
                                                    display:'flex',
                                                    alignItems:"center",
                                                    justifyContent:"space-between",
                                                    px:1,
                                                    "@media screen and (max-width: 47.9375em)" : {
                                                        flexWrap:"wrap",
                                                        height:"unset",
                                                        py:0.5,
                                                    }
                                                }}
                                            >
                                                <Text>
                                                    {pfv.productfield?.name}
                                                </Text>

                                                <Text>
                                                    {pfv.value.toString()}
                                                </Text>
                                            </Box>
                                        )
                                    })
                                }
                            </Box>
                        }

                    </Box>
                )}
            </SHQuickAction>
        </div>
    )
}

const CarrierRowReadOnly = (props) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 
    return (
        <SHQuickAction
            key={"scan-prod"+props.index}
            component={<ProductHoverDetails productId={props.row.product.id}  product={props.row.product}/>}
            
            sx={{
                modal:{
                    body:{
                        ml:2,
                    }
                }
            }}
        >
            {({anchorEl,onMouseEnter,onMouseLeave,onTouchStart}) => (
                <Box
                    className={cx('', {
                        'is-addon' : props.row.type === orderline_type.ADD_ON,
                        'is-serialnumber': props.row.serialnumbers?.length > 0,
                    })}
                    aria-owns={anchorEl ? 'simple-popover' : undefined}
                    aria-haspopup="true"
                    onMouseEnter={onMouseEnter}
                    onMouseLeave={onMouseLeave}
                    onTouchStart={onTouchStart} 
                    sx={{
                        flex:1,
                        display:"flex",
                        background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                        border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                        boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
                        borderRadius:3,
                        padding:1,
                        alignItems:"center",
                        justifyContent: "space-between",
                        flexDirection:'column',
                        animation: "1s pulsate",
                        "&.is-serialnumber" : {
                            background:colors.pastel.light.lightblue[100],
                            border: `1px solid ${colors.pastel.light.lightblue[200]}`
                        },
                        "&.is-addon": {
                            background:colors.pastel.light.orange[100],
                            border: `1px solid ${colors.pastel.light.orange[200]}`
                        }
                    }}
                >
                    <Box
                        sx={{
                            display:"flex",
                            alignItems:"center",
                            width:1,
                            
    
                        }}
                    >

                        <Box
                            sx={{
                                height:32,
                                px:1.5,
                                background:props.row.type !== orderline_type.ADD_ON ? colors.orange['100'] : colors.pastel.st.orange[200],
                                color:props.row.type !== orderline_type.ADD_ON ? colors.orange['400'] : colors.pastel.light.orange[600],
                                border: `1px solid ${props.row.type !== orderline_type.ADD_ON ? colors.orange[400] : colors.pastel.light.orange[600]}`,
                                // color:colors.pastel.light.lightblue[100],
                                borderRadius:4,
                                lineHeight:"32px",
                                fontWeight:500,
                                fontFamily:"Roboto, sans-serif",
                                fontSize:14,
                            }}
                        >
                            {props.row.amount}

                        </Box>
                        
                        <Box
                            sx={{
                                ml:2,
                                display:"flex",
                                flexDirection:'column',
                            }}
                        >
                            <Text
                                sx={{
                                    whiteSpace:"pre",
                                    color:props.row.type !== orderline_type.ADD_ON ? colors.txt["primary"] : theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['primary']
                                }} 
                            >
                                {props.row.product.barcodes?.[0].barcode}
                            </Text>
                            <Text semibold
                                sx={{

                                    color:props.row.type !== orderline_type.ADD_ON ? colors.txt["primary"] : theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt['primary']
                                }}
                            >
                                {props.row.product.description}
                            </Text>
                            {getVirtualProductNames(props.row).map(name => (
                                <ShLabel key={"vp"+name} size={24} fitted variant="purple" sx={{mt:0.5, alignSelf:"flex-start", height:"auto", minHeight:24, lineHeight:"18px", py:"2px", textAlign:"left"}}>
                                    {name}
                                </ShLabel>
                            ))}
                        </Box>

                        {props.row.serialnumbers?.length >0 &&
                            <Box
                                sx={{
                                    height:32,
                                    ml:"auto",
                                    px:1.5,
                                    mr:1,
                                    background:colors.pastel.light.lightblue[600],
                                    color:colors.pastel.light.lightblue[100],
                                    borderRadius:2,
                                    lineHeight:"32px",
                                    fontWeight:500,
                                    fontFamily:"Roboto, sans-serif",
                                    fontSize:14,
                                    my:"auto",
                                }}
                            >
                                {props.row.serialnumbers[0].barcode}
                            </Box>
                        }
                        {props.row.type === orderline_type.ADD_ON &&
                            <Box
                                sx={{
                                    height:32,
                                    ml:"auto",
                                    px:1.5,
                                    mr:1,
                                    background:colors.pastel.light.orange[600],
                                    color:colors.pastel.light.orange[100],
                                    borderRadius:2,
                                    lineHeight:"32px",
                                    fontWeight:500,
                                    fontFamily:"Roboto, sans-serif",
                                    fontSize:14,
                                    my:"auto",
                                }}
                            >
                                {t("add_on")}
                            </Box>
                        }

                    </Box>
                    {props.row.product?.productFieldValues?.some(
                        pfv => pfv.value !== null && pfv.value !== "" && pfv.value !== undefined &&
                            (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                    ) &&
                        <Box
                            sx={{
                                width:1,
                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[50],
                                display:'flex',
                                flexDirection:"column",
                                gap:1,
                                px:1.5,
                                py:1.5,
                                borderRadius:2,
                                mt:1.5,
                            }}
                        >
                            {props.row.product.productFieldValues
                                .filter(pfv =>
                                    pfv.value !== null &&
                                    pfv.value !== "" &&
                                    pfv.value !== undefined &&
                                    (!booleanValueTypes.includes(pfv.productfield.valueType) || toBoolean(pfv.value))
                                )
                                .map((pfv, pfvidex) => {
                                    return (
                                        booleanValueTypes.includes(pfv.productfield.valueType) ?
                                        <Box
                                            key={"pfv"  + pfvidex}
                                            sx={{
                                                height:24,
                                                border:`1px solid ${toBoolean(pfv.value) ? colors.green[400] : colors.red[400]}`,
                                                background: toBoolean(pfv.value) ? colors.green[100] : colors.red[100],
                                                borderRadius:3,
                                                display:'flex',
                                                alignItems:"center",
                                                justifyContent:"space-between",
                                                px:1,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flexWrap:"wrap",
                                                    height:"unset",
                                                    py:0.5,
                                                }
                                            }}
                                        >
                                            <Text>
                                                {pfv.productfield?.name}
                                            </Text>

                                            <Box
                                                sx={{
                                                    color: toBoolean(pfv.value) ? colors.green[400] : colors.red[400],
                                                    fontSize:14,
                                                    fontWeight:700,
                                                    fontFamily:"Roboto,sans-serif",
                                                    lineHeight:"16px",
                                                }}
                                            >
                                                {t(toBoolean(pfv.value) ? "yes" : 'no')}
                                            </Box>
                                        </Box>
                                        :
                                        <Box
                                            key={"pfv" + pfvidex}
                                            sx={{
                                                height:24,
                                                border:`1px solid ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                                                background:theme.palette.mode === "dark" ? colors.grey[400] : colors.grey[0],
                                                borderRadius:3,
                                                display:'flex',
                                                alignItems:"center",
                                                justifyContent:"space-between",
                                                px:1,
                                                "@media screen and (max-width: 47.9375em)" : {
                                                    flexWrap:"wrap",
                                                    height:"unset",
                                                    py:0.5,
                                                }
                                            }}
                                        >
                                            <Text>
                                                {pfv.productfield?.name}
                                            </Text>

                                            <Text>
                                                {pfv.value.toString()}
                                            </Text>
                                        </Box>
                                    )
                                })
                            }
                        </Box>
                    }

                </Box>
            )}
        </SHQuickAction>
    )
}


const EcheckCarrier = (props) => {
        
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 

  const dropId = props.partOfBundle !== null
    ? `bundle-${props.bundleIndex}-carrier-${props.carrierIndex}`
    : `carrier-${props.carrierIndex}`;

  const { setNodeRef, isOver } = useDroppable({ id: dropId });
    const isInBundle = useMemo(() => {
        return props.newBundle?.includes(props.carrierIndex);
    }, [props.newBundle, props.carrierIndex]);

    const toggleBundle = useCallback(() => {
        if (isInBundle) {
            props.setNewBundle(prev => prev.filter(i => i !== props.carrierIndex));
        } else {
            props.setNewBundle(prev => [...prev, props.carrierIndex]);
        }
    }, [isInBundle, props.setNewBundle, props.carrierIndex]);
    
    const style = {
        border: isOver ? '2px dashed #007bff' : '2px solid transparent',
        borderRadius:'16px',
        // padding: '12px',
        // marginBottom: '12px',
        background: isOver ? theme.palette.mode === "dark" ?  colors.grey[800] : colors.grey[0] : theme.palette.mode === "dark" ?  colors.grey[500] : colors.bg['tertiary'],
    };

    // Undo scan state + handler
    const [undoModalOpen, setUndoModalOpen] = useState(false);
    const [undoModalRow, setUndoModalRow] = useState(null);

    const handleUndoClick = (row, carrierIndex, bundleIndex) => {
        const hasSerial = Array.isArray(row.serialnumbers) && row.serialnumbers.length > 0;
        const siblingSerialCount = props.carrier.rows.filter(r =>
            r.product.barcode === row.product.barcode &&
            Array.isArray(r.serialnumbers) && r.serialnumbers.length > 0
        ).length;

        // Match a row by value (barcode + serial + externalId + type) instead of reference
        // because React state updates can create new object references
        const findRowIndex = (rows) => {
            return rows.findIndex(r => {
                if (r.product.barcode !== row.product.barcode) return false;
                const rSerial = r.serialnumbers?.[0];
                const rowSerial = row.serialnumbers?.[0];
                const serialMatch = !rSerial || !rowSerial || rSerial === rowSerial;
                const externalIdMatch =
                    (r.externalId && row.externalId)
                        ? r.externalId === row.externalId
                        : true;
                const typeMatch = r.type === row.type;
                return serialMatch && externalIdMatch && typeMatch;
            });
        };

        // Direct undo when amount is 1 and no grouped serial selection needed
        if (row.amount === 1 && siblingSerialCount <= 1) {
            if (props.clearInstructionShown) props.clearInstructionShown(row.product?.sku);
            props.setCarriers(prev => {
                const updatedCarriers = [...prev];
                if (bundleIndex !== undefined && bundleIndex !== null && bundleIndex !== -1) {
                    const updatedBundle = { ...updatedCarriers[bundleIndex] };
                    const updatedSubCarriers = [...updatedBundle.carriers];
                    const targetRows = [...updatedSubCarriers[carrierIndex].rows];
                    const idx = findRowIndex(targetRows);
                    if (idx !== -1) targetRows.splice(idx, 1);
                    updatedSubCarriers[carrierIndex] = {
                        ...updatedSubCarriers[carrierIndex],
                        rows: targetRows,
                    };
                    updatedBundle.carriers = updatedSubCarriers;
                    updatedCarriers[bundleIndex] = updatedBundle;
                } else {
                    const targetRows = [...updatedCarriers[carrierIndex].rows];
                    const idx = findRowIndex(targetRows);
                    if (idx !== -1) targetRows.splice(idx, 1);
                    updatedCarriers[carrierIndex] = {
                        ...updatedCarriers[carrierIndex],
                        rows: targetRows,
                    };
                }
                return updatedCarriers;
            });

            // Restore to originalOrderlines (no nested setToScanRows)
            if (props.setOriginalOrderlines) {
                props.setOriginalOrderlines(prevOriginal => {
                    const updatedOriginal = [...(prevOriginal || [])];
                    updatedOriginal.push({ ...row, amount: 1 });
                    return updatedOriginal;
                });
            }

            // Add back to toScanRows - match by barcode and type since display is merged by SKU+type
            props.setToScanRows(prev => {
                const updatedRows = [...(prev || [])];
                const existingIndex = updatedRows.findIndex(r =>
                    r.product.barcode === row.product.barcode &&
                    r.type === row.type
                );
                if (existingIndex !== -1) {
                    updatedRows[existingIndex] = {
                        ...updatedRows[existingIndex],
                        amount: updatedRows[existingIndex].amount + 1
                    };
                } else {
                    updatedRows.push({ ...row, amount: 1 });
                }
                return updatedRows;
            });

            // Restore serial number if applicable
            if (hasSerial) {
                props.setSkuSerialMap(prevMap => {
                    const sku = row.product.sku;
                    const serial = row.serialnumbers[0];
                    if (!sku || !serial) return prevMap;
                    const newMap = { ...prevMap };
                    if (!newMap[sku]) newMap[sku] = [];
                    newMap[sku] = [...newMap[sku], serial];
                    return newMap;
                });
            }

            props.setHighlightColor('green');
            if(props.playSuccess) props.playSuccess();
        } else {
            // Open modal for amount selection or grouped serial selection
            setUndoModalRow(row);
            setUndoModalOpen(true);
        }
    };

    const isDraggingRowFromThisCarrier = useMemo(() => {
        if (!props.activeDragItem) return false;

        const match = props.activeDragItem.match(/(?:bundle-(\d+)-)?carrier-(\d+)-row-(\d+)/);
        if (!match) return false;

        const [, bundleIdx, carrierIdx, rowIdx] = match.map(Number);

        // If this carrier matches the drag source:
        const isSameCarrier =
            Number(props.carrierIndex) === carrierIdx &&
            (props.bundleIndex ?? null) === (Number.isNaN(bundleIdx) ? null : bundleIdx);

        if (!isSameCarrier) return false;

        const row = props.carrier.rows[rowIdx];
        if (!row) return false;

        // Will dragging this row make this carrier appear empty?
        return props.carrier.rows.length === 1 && row.amount === 1;
    }, [props.activeDragItem, props.carrier, props.carrierIndex, props.bundleIndex]);

    const setCarrierDetails = (newCarrier) => {
        if (props.partOfBundle) {
            props.setCarriers((prev) => {
                const updated = [...prev];
                const currentBundle = updated[props.bundleIndex];
                const updatedCarriers = [...currentBundle.carriers];

                const prevCarrier = updatedCarriers[props.carrierIndex];

                // Update the specific carrier
                const updatedCarrier = {
                    ...prevCarrier,
                    ...newCarrier,
                };

                updatedCarriers[props.carrierIndex] = updatedCarrier;

      
                const remainingItems = props.toScanRows?.length || 0;
                const hasEmptyCarrier = updatedCarriers.some(
                    (c) => c.carrierId === null && (!c.rows || c.rows.length === 0)
                );
                const atMax = updatedCarriers.length >= props.maxCarrierAmount;

                let pushedNew = false;

                if (
                    remainingItems > 0 &&
                    !hasEmptyCarrier &&
                    !atMax &&
                    updatedCarrier.rows &&
                    updatedCarrier.rows.length > 0 
                ) {
                    updatedCarriers.push({
                        carrierId: null,
                        bundle: false,
                        carrierName: "",
                        totalProducts: 0,
                        totalWeight: 0,
                        deliveryOption: null,
                        dimensions: null,
                        rows: [],
                    });

                    pushedNew = true;
                }

                updated[props.bundleIndex] = {
                    ...currentBundle,
                    carriers: updatedCarriers,
                };

                // Set carrier to add to
                if (pushedNew) {
                    props.setCarrierToAddTo({
                        bundleIndex: -1,
                        carrierIndex: updatedCarriers.length - 1, // last one
                    });
                } else if (!updatedCarrier.rows || updatedCarrier.rows.length === 0) {
                    props.setCarrierToAddTo({
                        bundleIndex: props.bundleIndex,
                        carrierIndex: props.carrierIndex,
                    });
                }

                if(props.carrierMandatory){
                    if(props.toScanRows.length === 0 && props.selectedShipper && props.selectedShipperProfile && props.autoFinalize) {
                        const hasUnassignedCarrier = updated.some(carrier => {
                            if (carrier.bundle) {
                                return carrier.carriers.some(sub => sub.carrierId === null);
                            }
                            return carrier.carrierId === null;
                        });
                        if(!hasUnassignedCarrier) {
                            props.setGoFinalize(true);
                        }
                    }
                }

                return updated;
            });
        } else {
            props.setCarriers((prev) => {
                const updated = [...prev];
                const prevCarrier = updated[props.carrierIndex];

                const updatedCarrier = {
                    ...prevCarrier,
                    ...newCarrier,
                };

                updated[props.carrierIndex] = updatedCarrier;

                const remainingItems = props.toScanRows?.length || 0;
                const hasEmptyCarrier = updated.some(
                    (c) => c.carrierId === null && (!c.rows || c.rows.length === 0)
                );

                const atMax = updated.length >= props.maxCarrierAmount;

                let pushedNew = false;

                if (
                    remainingItems > 0 &&
                    !hasEmptyCarrier &&
                    !atMax &&
                    updatedCarrier.rows &&
                    updatedCarrier.rows.length > 0
                ) {
                    updated.push({
                        carrierId: null,
                        bundle: false,
                        carrierName: "",
                        totalProducts: 0,
                        totalWeight: 0,
                        deliveryOption: null,
                        dimensions: null,
                        rows: [],
                    });

                    pushedNew = true;
                }

                // Set carrier to add to
                if (pushedNew) {
                    props.setCarrierToAddTo({
                        bundleIndex: -1,
                        carrierIndex: updated.length - 1, // last one
                    });
                } else if (!updatedCarrier.rows || updatedCarrier.rows.length === 0) {
                    props.setCarrierToAddTo({
                        bundleIndex: -1,
                        carrierIndex: props.carrierIndex,
                    });
                }
                
                if(props.carrierMandatory){
                    if(props.toScanRows.length === 0 && props.selectedShipper && props.selectedShipperProfile && props.autoFinalize) {
                        const hasUnassignedCarrier = updated.some(carrier => {
                            if (carrier.bundle) {
                                return carrier.carriers.some(sub => sub.carrierId === null);
                            }
                            return carrier.carrierId === null;
                        });
                        if(!hasUnassignedCarrier) {
                            props.setGoFinalize(true);
                        }
                    }
                }

                return updated;
            });
        }
        if(props.playSuccess) props.playSuccess();
    }
    const setCarrierDimensions = (dimensions) => {
        if (props.partOfBundle) {
            props.setCarriers((prev) => {
                const updated = [...prev];
                const currentBundle = updated[props.bundleIndex];

                const updatedCarriers = [...currentBundle.carriers];
                const prevCarrier = updatedCarriers[props.carrierIndex];

                updatedCarriers[props.carrierIndex] = {
                    ...prevCarrier,
                    dimensions: {
                        ...prevCarrier.dimensions,
                        ...dimensions,
                    },
                };

                updated[props.bundleIndex] = {
                    ...currentBundle,
                    carriers: updatedCarriers,
                };

                return updated;
            });
        } else {
            props.setCarriers((prev) => {
                const updated = [...prev];
                const prevCarrier = updated[props.carrierIndex];

                updated[props.carrierIndex] = {
                    ...prevCarrier,
                    dimensions: {
                        ...prevCarrier.dimensions,
                        ...dimensions,
                    },
                };

                return updated;
            });
        }
    };


    const [updateQuarantineModalOpen, setUpdateQuarantineModalOpen] = useState(false);
    const updateQuarantineModal = {
        open: updateQuarantineModalOpen,
        setOpen: setUpdateQuarantineModalOpen,
    }

    const [shipperOverrideModalOpen, setShipperOverrideModalOpen] = useState(false);

    const applyShipperOverride = (override) => {
        if (props.partOfBundle) {
            props.setCarriers(prev => {
                const updated = [...prev];
                const currentBundle = updated[props.bundleIndex];
                const updatedSubCarriers = [...currentBundle.carriers];
                updatedSubCarriers[props.carrierIndex] = {
                    ...updatedSubCarriers[props.carrierIndex],
                    ...override,
                };
                updated[props.bundleIndex] = {
                    ...currentBundle,
                    carriers: updatedSubCarriers,
                };
                return updated;
            });
        } else {
            props.setCarriers(prev => {
                const updated = [...prev];
                updated[props.carrierIndex] = {
                    ...updated[props.carrierIndex],
                    ...override,
                };
                return updated;
            });
        }
    };

    const clearShipperOverride = () => {
        if (props.partOfBundle) {
            props.setCarriers(prev => {
                const updated = [...prev];
                const currentBundle = updated[props.bundleIndex];
                const updatedSubCarriers = [...currentBundle.carriers];
                const { shipperId, shipperProfileId, shipperName, shipperProfileName, shipperType, ...rest } = updatedSubCarriers[props.carrierIndex];
                updatedSubCarriers[props.carrierIndex] = rest;
                updated[props.bundleIndex] = {
                    ...currentBundle,
                    carriers: updatedSubCarriers,
                };
                return updated;
            });
        } else {
            props.setCarriers(prev => {
                const updated = [...prev];
                const { shipperId, shipperProfileId, shipperName, shipperProfileName, shipperType, ...rest } = updated[props.carrierIndex];
                updated[props.carrierIndex] = rest;
                return updated;
            });
        }
    };

    const canShowShipperOverride = props.hasAllowedToSetCustomCarrierShipperPermission && !props.isBundling && !props.partOfBundle;
    const hasShipperOverride = Boolean(props.carrier?.shipperId && props.carrier?.shipperProfileId);

    const prevSelectedShipperIdRef = useRef(props.selectedShipper?.id);
    useEffect(() => {
        const nextId = props.selectedShipper?.id;
        if (prevSelectedShipperIdRef.current !== nextId) {
            if (hasShipperOverride) clearShipperOverride();
            prevSelectedShipperIdRef.current = nextId;
        }
    }, [props.selectedShipper?.id, hasShipperOverride]);

    return (
        <div ref={setNodeRef} style={style}>
            <Box
                key={props.carrierIndex + props.carrierToAddTo === null + props.carrier.carrierName}
                onClick={() => {
                    if(props.partOfBundle){
                        props.setCarrierToAddTo({
                            bundleIndex:props.bundleIndex,
                            carrierIndex:props.carrierIndex,
                        })
                    } else {
                        props.setCarrierToAddTo({
                            bundleIndex:-1,
                            carrierIndex:props.carrierIndex,
                        })

                    }
                }}
                className={cx("",{
                    'active':(props.carrierToAddTo === null && props.carrier.carrierName === "") || (props.partOfBundle? props.bundleIndex===props.carrierToAddTo?.bundleIndex && props.carrierIndex===props.carrierToAddTo?.carrierIndex : props.carrierIndex===props.carrierToAddTo?.carrierIndex && props.carrierToAddTo.bundleIndex === -1),
                })}
                sx={{
                    background: theme.palette.mode === "dark" ? (isInBundle && props.isBundling) ? colors.blue[100] : colors.grey[500] : (isInBundle && props.isBundling) ? colors.primary[100] : colors.bg["tertiary"],
                    border: (isInBundle && props.isBundling) && `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                    borderRadius: 4,
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    paddingBottom: 3,
                    postion:"relative",
                    px:2,
                    py:2,
                    "&.active":{
                        background:theme.palette.mode==="dark" ? colors.blue[100] : colors.primary[100],
                        border:`1px solid ${theme.palette.mode==="dark" ? colors.blue[400] : colors.primary[400]}`,
                    }
                }}
            >
                {/* {!props.readOnly && props.selectedShipper?.type === "bol" && props.carrier.rows.length > 0 && 
                    <Box
                        sx={{
                            height: 36,
                            display: "flex",
                            alignItems:"center",
                            justifyContent:"space-between",
                            boxShadow: ' 0px 1px 2px 0px rgba(49, 65, 95, 0.25)',
                            pr:0.5,
                            pl:3,
                            background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[0],
                            borderRadius:6,
                            "@media screen and (max-width: 47.9375em)" : {
                                display:"none"
                            },
                            mb:3,
                        }}
                    >
                        <Box
                            sx={{
                                mr:3,
                                display:"flex",
                                alignItems:"center",
                                gap:2,
                            }}
                        >
                            <Text light>{t("label")}:</Text>
                            <Text semibold>{props.carrier.deliveryOption ? `${props.carrier.deliveryOption.labelDisplayName} (€ ${props.carrier.deliveryOption.labelPrice.totalPrice})` : t("no_label")}</Text>
                        </Box>
                        <ButtonBase
                            sx={{
                                width:32,
                                height:32,
                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.txt["primary"],
                                color:"#fff",
                                borderRadius:5,
                            }}
                        >
                            
                            
                            <DropdownBase
                                displayName={"labelDisplayName"} 
                                options={props.deliveryOptions[props.carrierIndex] || []}
                                onChange={(selected) => props.selectLabel(props.carrierIndex,selected)}
                                selected={props.carrier.deliveryOption ? props.deliveryOptions[props.carrierIndex].indexOf(props.carrier.deliveryOption) : -1}
                                noSelection={t("no_label")} 
                                maxHeight={"unset"}
                                useTranslation 
                                closeOnSelection
                                disallowDeselection
                                stopPropagation
                                defaultStyling
                                shComponent={<CustomComponent/>} // new
                            >
                                    
                                <EditOutlinedIcon/>
                            </DropdownBase>
                        </ButtonBase>
                    </Box>
                } */}
                <Box
                    sx={{
                        display:"flex",
                        justifyContent:"space-between",
                        alignItems:'flex-start'
                    }}
                >
                    <Box
                        sx={{
                            pb:2,
                        }}
                    >
                        <Text semibold>
                            {props.carrier.carrierName === '' ? "Scan drager ..." : 
                            <DropdownBaseV2
                                transformOrigin={{
                                    vertical: "bottom"
                                }}
                                anchorOrigin={{
                                    vertical:"top"
                                }}
                                displayName={"name"}
                                searchOptions={{
                                    enabled:true,
                                    placeholder: t("search")
                                }}
                                onChange={(selected) => {
                                    if(selected !== -1){
                                        const chosenCarrier = props.carriersSelect[selected];
                                        const newCarrier = {
                                            carrierId: chosenCarrier.id,
                                            carrierName: chosenCarrier.name,
                                            dimensions: {
                                                width: chosenCarrier.width,
                                                height: chosenCarrier.height,
                                                length: chosenCarrier.length,
                                                weight: chosenCarrier.weight,
                                                isFlexible: chosenCarrier.isFlexible,
                                                type:chosenCarrier.type,
                                            }
                                        };

                                        
                                        if (props.partOfBundle) {
                                            props.setCarriers((prev) => {
                                                const updated = [...prev];

                                                const currentBundle = updated[props.bundleIndex];
                                                const updatedCarriers = currentBundle.carriers.map((carrier, i) =>
                                                    i === props.carrierIndex
                                                        ? { ...carrier, ...newCarrier }
                                                        : carrier
                                                );

                                                updated[props.bundleIndex] = {
                                                    ...currentBundle,
                                                    carriers: updatedCarriers,
                                                };

                                                if(props.carrierMandatory){
                                                    if(props.toScanRows.length === 0 && props.selectedShipper && props.selectedShipperProfile && props.autoFinalize) {
                                                        const hasUnassignedCarrier = updated.some(carrier => {
                                                            if (carrier.bundle) {
                                                                return carrier.carriers.some(sub => sub.carrierId === null);
                                                            }
                                                            return carrier.carrierId === null;
                                                        });
                                                        if(!hasUnassignedCarrier) {
                                                            props.setGoFinalize(true);
                                                        }
                                                    }
                                                }

                                                return updated;
                                            });
                                        } else {
                                            props.setCarriers((prev) => {
                                                const updated = [...prev];

                                                updated[props.carrierIndex] = {
                                                    ...updated[props.carrierIndex],
                                                    ...newCarrier,
                                                };

                                                if(props.carrierMandatory){
                                                    if(props.toScanRows.length === 0 && props.selectedShipper && props.selectedShipperProfile && props.autoFinalize) {
                                                        const hasUnassignedCarrier = updated.some(carrier => {
                                                            if (carrier.bundle) {
                                                                return carrier.carriers.some(sub => sub.carrierId === null);
                                                            }
                                                            return carrier.carrierId === null;
                                                        });
                                                        if(!hasUnassignedCarrier) {
                                                            props.setGoFinalize(true);
                                                        }
                                                    }
                                                }

                                                return updated;
                                            });
                                        }

                                        if(props.playSuccess) props.playSuccess();
                                    }
                                    // props.selectManualCarrier(selected)
                                }}
                                noSelection={t("choose_carrier")} //Custom text when nothing is selected
                                options={props.carriersSelect}
                                selected={props.carriersSelect?.findIndex(carrier => carrier.id === props.carrier.carrierId)}
                                closeOnSelection
                                resetOnSelection
                                disallowDeselection
                                stopPropagation
                                noPropagationOnSelect
                            >
                                {({anchorEl,onClick}) => (
                                    <ButtonBase
                                        onClick={onClick}
                                        sx={{
                                            height:32,
                                            px:2,
                                            ml:"-16px",
                                            fontSize:14,
                                            borderRadius:4,
                                            overflow:"hidden",
                                            fontWeight:500,
                                        }}
                                    >
                                        {props.carrier.carrierName}
                                    </ButtonBase>   
                                )}
                            </DropdownBaseV2>
                            // props.carrier.carrierName
                            }
                        </Text>
                        <Box pt={0.5}>
                            <Text bold>{props.carrier.totalProducts}</Text> <Text light>Producten,</Text>&nbsp;
                            <Text bold>{formatNumber(i18n,props.carrier.totalWeight/1000)}</Text> <Text light>kg</Text>

                        </Box>
                    </Box>
                    
                    {!props.isBundling && props.carrier?.dimensions?.type === "pallet" && (
                        <ButtonBase
                            onClick={() => updateQuarantineModal.setOpen(true)}
                            sx={{
                                borderRadius:4,
                                height:32,
                                px:2,
                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                color:theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                                fontSize:14,
                                fontWeight:500,
                                fontFamily:"Roboto,sans-serif",
                                gap:1
                            }}
                        >
                            {t("edit_carrier_dimensions")}<EditOutlinedIcon></EditOutlinedIcon>
                        </ButtonBase>
                    )}
                    {(canShowShipperOverride && (props.selectedShipper?.type === "postnl" || props.selectedShipper?.type === "myparcel")) && (
                        <ButtonBase
                            onClick={(event) => {
                                event.stopPropagation();
                                setShipperOverrideModalOpen(true);
                            }}
                            sx={{
                                borderRadius:4,
                                height:32,
                                px:2,
                                background: hasShipperOverride
                                    ? getShipperColorSettingsByType(props.carrier?.shipperType).base
                                    : (theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400]),
                                color: hasShipperOverride
                                    ? getShipperColorSettingsByType(props.carrier?.shipperType).background
                                    : (theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']),
                                fontSize:14,
                                fontWeight:500,
                                fontFamily:"Roboto,sans-serif",
                                gap:1,
                            }}
                        >
                            <LocalShippingOutlinedIcon/>
                        </ButtonBase>
                    )}
                    {(props.isBundling && !props.partOfBundle) &&
                    <Checkbox
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                        checked={isInBundle}
                        onChange={toggleBundle}
                        sx={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor:   theme.palette.mode === "dark" ? isInBundle ? colors.blue[400] : colors.blue[100] : isInBundle ? colors.primary[400] : colors.primary[100],
                            // color:"#fff",
                            border: `1px solid ${theme.palette.mode === "dark" ? colors.blue[400] : colors.primary[400]}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '& .MuiSvgIcon-root': {
                                fontSize: 20,
                                color: theme.palette.mode === "dark" ? isInBundle ? colors.txt["primaryDark"] : colors.blue[400] : isInBundle ? colors.txt["light"] : colors.primary[400],
                            },
                            '&:hover': {
                                backgroundColor:  theme.palette.mode === "dark" ?  colors.blue[400] : colors.primary[400],
                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            },
                            '&:hover .MuiSvgIcon-root': {
                                fontSize: 20,
                                color: theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            },
                        }}
                    />
                    } 
                </Box>
                <Box
                    sx={{
                        display:'flex',
                        flexDirection:'column',
                        gap:1.5,
                    }}
                >
                    {hasShipperOverride &&
                        <Box
                            sx={{
                                display:'flex',
                                alignItems:'center',
                                justifyContent:'space-between',
                                width:1,
                                fontSize:14,
                                fontFamily:"Roboto, sans-serif",
                                p:1.5,
                                borderRadius:2,
                                background: getShipperColorSettingsByType(props.carrier?.shipperType).background,
                                color:getShipperColorSettingsByType(props.carrier?.shipperType).base,
                                border: `1px solid ${getShipperColorSettingsByType(props.carrier?.shipperType).base}`,
                            }}
                        >
                            <Box
                                sx={{
                                    display:'flex',
                                    flexDirection:'column',
                                }}
                            >
                                <b>{props.carrier.shipperName}</b> 
                                {props.carrier.shipperProfileName}
                            </Box>
                            <Shbutton
                                size={"32"}
                                color={"red"}
                                variant="contained"
                                onClick={clearShipperOverride}
                            >
                                {t("delete")}
                            </Shbutton>
                        </Box>
                    }

                    {props.carrier.rows.map((row, index) => {
                        // Compose dragId like:
                        // If in bundle: "bundle-{bundleIndex}-carrier-{carrierIndex}-row-{rowIndex}"
                        // If not in bundle: "carrier-{carrierIndex}-row-{rowIndex}"
                        const dragId = props.partOfBundle
                        ? `bundle-${props.bundleIndex}-carrier-${props.carrierIndex}-row-${index}`
                        : `carrier-${props.carrierIndex}-row-${index}`;

                        return (
                            <CarrierRow
                                playError={props.playError}
                                playSuccess={props.playSuccess}
                                key={index}
                                dragId={dragId}
                                row={row}
                                carrierIndex={props.carrierIndex}
                                bundleIndex={props.bundleIndex}
                                rowIndex={index}
                                carrier={props.carrier}
                                setCarriers={props.setCarriers}
                                onUndoClick={handleUndoClick}
                            />
                        )
                    })}
                    {/* || (props.carrier.rows.lengt === 1 && isDragging && props.carrier.rows[0].amount - 1 === 0) */}
                    {(props.carrier.rows.length === 0 || isDraggingRowFromThisCarrier) && 
                        <Box
                            sx={{
                                flex:1,
                                display:"flex",
                                background:theme.palette.mode === "dark" ? colors.grey[600] : colors.grey[0],
                                border: theme.palette.mode === "light" ? `1px solid ${colors.grey[100]}` :null,
                                boxShadow: theme.palette.mode === "light" ? "0 2px 4px rgba(49,65,95,.15)" : null,
                                borderRadius:3,
                                padding:1,
                                alignItems:"center",
                                justifyContent: "center",
                                opacity:0.7,
                            }}
                        >
                            <Text semibold sx={{marginLeft: 2,
                                opacity:0.7,}}>
                                {t("scan_an_order")}
                            </Text>
                        </Box>
                    }
                </Box>
                {props.carrier.carrierName === "" &&
                    <Box sx={{pt:3,
                        display:'flex',
                        gap:2,
                    }}>
                        <DropdownBaseV2
                            stopPropagation
                            transformOrigin={{
                                vertical: "bottom"
                            }}
                            anchorOrigin={{
                                vertical:"top"
                            }}
                            searchOptions={{
                                enabled:true,
                                placeholder: t("search")
                            }}
                            displayName={"name"} 
                            onChange={(selected) => {
                                if(selected !== -1){
                                    const chosenCarrier = props.carriersSelect[selected];
                                    const newCarrier = {
                                        carrierId: chosenCarrier.id,
                                        carrierName: chosenCarrier.name,
                                        type:chosenCarrier.type,
                                        dimensions: {
                                            width: chosenCarrier.width,
                                            height: chosenCarrier.height,
                                            length: chosenCarrier.length,
                                            weight: chosenCarrier.weight,
                                            isFlexible: chosenCarrier.isFlexible,
                                            type:chosenCarrier.type,
                                        }
                                    };
                                    setCarrierDetails(newCarrier);
                                }
                                // console.log(props.bundleIndex);
                                // console.log(props.carrierIndex);
                                // props.selectManualCarrier(selected)
                            }}
                            noSelection={t("choose_carrier")} //Custom text when nothing is selected
                            options={props.carriersSelect}
                            selected={-1}
                            closeOnSelection
                            resetOnSelection
                            noPropagationOnSelect
                        >
                            {({anchorEl,onClick}) => (
                                <Shbutton 
                                    className={"group-btn"}  
                                    sx={{whiteSpace:"nowrap"}} 
                                    color={"primaryGrey"} 
                                    variant="contained"
                                    onClick={onClick}
                                    stopPropagation
                                >
                                    {t("choose_carrier")}
                                </Shbutton>
                            )}
                        </DropdownBaseV2>

                        <Shbutton 
                            className={"group-btn"}  
                            sx={{whiteSpace:"nowrap"}} 
                            color={"primaryGrey"} 
                            variant="contained" 
                            onClick={(event) => {
                                event.stopPropagation();
                                const newCarrier = {
                                    carrierId: -1,
                                    carrierName: t("own_carrier"),
                                    dimensions: null,
                                };
                                setCarrierDetails(newCarrier);
                                // props.OwnCarrierClick();
                            }}
                        >
                            {t("own_carrier")}
                        </Shbutton>
                    </Box>
                }
            </Box>

            <MDynamicValues
                open={updateQuarantineModal.open}
                handleClose={() => updateQuarantineModal.setOpen(false)}
                title={t("edit_carrier_dimensions")}
                labelWidth={144}
                fields={[
                    { name: "length", label: t("length"), type: "integer", defaultValue: props.carrier?.dimensions?.length ?? 0},
                    { name: "width", label: t("width"), type: "integer", defaultValue: props.carrier?.dimensions?.width ?? 0},
                    { name: "height", label: t("height"), type: "integer", defaultValue: props.carrier?.dimensions?.height ?? 0}
                ]}
                onConfirm={setCarrierDimensions}
            />

            <MEcheckUndoAmount
                open={undoModalOpen}
                onClose={() => {
                    setUndoModalOpen(false);
                    setUndoModalRow(null);
                }}
                playSuccess={props.playSuccess}
                playError={props.playError}
                row={undoModalRow}
                carrier={props.carrier}
                carrierIndex={props.carrierIndex}
                bundleIndex={props.bundleIndex}
                setCarriers={props.setCarriers}
                setToScanRows={props.setToScanRows}
                setOriginalOrderlines={props.setOriginalOrderlines}
                setSkuSerialMap={props.setSkuSerialMap}
                setHighlightColor={props.setHighlightColor}
                clearInstructionShown={props.clearInstructionShown}
            />

            <MEcheckCarrierShipper
                open={shipperOverrideModalOpen}
                handleClose={() => setShipperOverrideModalOpen(false)}
                selectedShipper={props.selectedShipper}
                currentShipperProfileId={props.carrier?.shipperProfileId}
                onConfirm={applyShipperOverride}
                onClear={clearShipperOverride}
            />
        </div>
    );
}

export default EcheckCarrier;



export const EcheckCarrierBundle = (props) => {

    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);


    const setCarrierDimensions = (dimensions) => {

        props.setCarriers((prev) => {
            const updated = [...prev];

            updated[props.carrierIndex] = {
                ...updated[props.carrierIndex],
                dimensions: {
                    ...updated[props.carrierIndex].dimensions,
                    ...dimensions,
                },
            };

            return updated;
        });

    };


    const [updateQuarantineModalOpen, setUpdateQuarantineModalOpen] = useState(false);
    const updateQuarantineModal = {
        open: updateQuarantineModalOpen,
        setOpen: setUpdateQuarantineModalOpen,
    }

    const [shipperOverrideModalOpen, setShipperOverrideModalOpen] = useState(false);

    const applyShipperOverride = (override) => {
        props.setCarriers(prev => {
            const updated = [...prev];
            updated[props.carrierIndex] = {
                ...updated[props.carrierIndex],
                ...override,
            };
            return updated;
        });
    };

    const clearShipperOverride = () => {
        props.setCarriers(prev => {
            const updated = [...prev];
            const { shipperId, shipperProfileId, shipperName, shipperProfileName, shipperType, ...rest } = updated[props.carrierIndex];
            updated[props.carrierIndex] = rest;
            return updated;
        });
    };

    const canOverrideShipper = props.hasAllowedToSetCustomCarrierShipperPermission;

    const hasShipperOverride = Boolean(props.carrier?.shipperId && props.carrier?.shipperProfileId);

    const prevSelectedShipperIdRef = useRef(props.selectedShipper?.id);
    useEffect(() => {
        const nextId = props.selectedShipper?.id;
        if (prevSelectedShipperIdRef.current !== nextId) {
            if (hasShipperOverride) clearShipperOverride();
            prevSelectedShipperIdRef.current = nextId;
        }
    }, [props.selectedShipper?.id, hasShipperOverride]);

    return (
        <Box
            key={props.carrierIndex}
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                border: `1px solid  ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                p:2,
                borderRadius:6,
                display:'flex',
                gap:1.5,
                flexDirection:'column',
            }}
        >
            <Box
                sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    alignItems:'flex-start'
                }}
            >
                <Box>
                    <Text semibold>
                        {props.carrier.carrierName === '' ? "Scan drager ..." : 

                        <DropdownBase
                            transformOrigin={{
                                vertical: "bottom"
                            }}
                            anchorOrigin={{
                                vertical:"top"
                            }}

                            searchOptions={{
                                enabled:true,
                                placeholder: t("search")
                            }}
                            displayName={"name"} 
                            onChange={(selected) => {
                                if(selected !== -1){
                                    const chosenCarrier = props.carriersSelect[selected];
                                    const newCarrier = {
                                        carrierId: chosenCarrier.id,
                                        carrierName: chosenCarrier.name,
                                    };

                                    props.setCarriers((prev) => {
                                        const updated = [...prev];

                                        updated[props.carrierIndex] = {
                                            ...updated[props.carrierIndex],
                                            ...newCarrier,
                                        };

                                        return updated;
                                    });
                                    if(props.playSuccess) props.playSuccess();
                                }
                                // props.selectManualCarrier(selected)
                            }}
                            noSelection={t("choose_carrier")} //Custom text when nothing is selected
                            options={props.carriersSelect}
                            selected={props.carriersSelect?.findIndex(carrier => carrier.id === props.carrier.carrierId)}
                            closeOnSelection
                            resetOnSelection
                            disallowDeselection
                        >
                            <ButtonBase
                                sx={{
                                    height:32,
                                    px:2,
                                    ml:"-16px",
                                    fontSize:14,
                                    borderRadius:4,
                                    overflow:"hidden",
                                    fontWeight:500,
                                }}
                            >
                                {props.carrier.carrierName}
                            </ButtonBase>   
                        </DropdownBase>
                        // props.carrier.carrierName
                        }
                    </Text>
                    <Box>
                        <Text bold>{props.carrier.totalProducts}</Text> <Text light>Producten,</Text>&nbsp;
                        <Text bold>{formatNumber(i18n,props.carrier.totalWeight/1000)}</Text> <Text light>kg</Text>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display:'flex',
                        alignItems:'center',
                        gap:1,
                    }}
                >

                    {!props.isBundling && props.carrier?.dimensions?.type === "pallet" && (
                        <ButtonBase
                            onClick={() => updateQuarantineModal.setOpen(true)}
                            sx={{
                                borderRadius:4,
                                height:32,
                                px:2,
                                background:theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400],
                                color:theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light'],
                                fontSize:14,
                                fontWeight:500,
                                fontFamily:"Roboto,sans-serif",
                                gap:1
                            }}
                        >
                            {t("edit_carrier_dimensions")}<EditOutlinedIcon></EditOutlinedIcon>
                        </ButtonBase>
                    )}
                    {(!props.isBundling && canOverrideShipper && (props.selectedShipper?.type === "postnl" || props.selectedShipper?.type === "myparcel")) && (
                        <ButtonBase
                            onClick={(event) => {
                                event.stopPropagation();
                                setShipperOverrideModalOpen(true);
                            }}
                            sx={{
                                borderRadius:4,
                                height:32,
                                px:2,
                                background: hasShipperOverride
                                    ? getShipperColorSettingsByType(props.carrier?.shipperType).base
                                    : (theme.palette.mode === "dark" ? colors.txt["secondary"] : colors.grey[400]),
                                color: hasShipperOverride
                                    ? getShipperColorSettingsByType(props.carrier?.shipperType).background
                                    : (theme.palette.mode === "dark" ? colors.txt['primary'] : colors.txt['light']),
                                fontSize:14,
                                fontWeight:500,
                                fontFamily:"Roboto,sans-serif",
                                gap:1,
                            }}
                        >
                            <LocalShippingOutlinedIcon/>
                        </ButtonBase>
                    )}
                    <ButtonBase
                        onClick={() => {

                            props.setCarriers(prev => {
                                // Remove the bundle
                                const withoutBundle = prev.filter(c => c !== props.carrier);

                                // Add back the individual carriers from the bundle
                                return [...withoutBundle, ...props.carrier.carriers];
                            });
                        }}

                        sx={{
                            
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            backgroundColor:   colors.red[400],
                            color:theme.palette.mode === "dark" ? colors.txt["primaryDark"] : colors.txt["light"],
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <DeleteIcon />
                    </ButtonBase>
                </Box>
            </Box>


            {hasShipperOverride &&
                <Box
                    sx={{
                        display:'flex',
                        alignItems:'center',
                        justifyContent:'space-between',
                        width:1,
                        fontSize:14,
                        fontFamily:"Roboto, sans-serif",
                        p:1.5,
                        borderRadius:2,
                        background: getShipperColorSettingsByType(props.carrier?.shipperType).background,
                        color:getShipperColorSettingsByType(props.carrier?.shipperType).base,
                        border: `1px solid ${getShipperColorSettingsByType(props.carrier?.shipperType).base}`,
                    }}
                >
                    <Box
                        sx={{
                            display:'flex',
                            flexDirection:'column',
                        }}
                    >
                        <b>{props.carrier.shipperName}</b>
                        {props.carrier.shipperProfileName}
                    </Box>
                    <Shbutton
                        size="32"
                        color={"red"}
                        variant="contained"
                        onClick={clearShipperOverride}
                    >
                        {t("delete")}
                    </Shbutton>
                </Box>
            }
            {props.carrier.carriers && props.carrier.carriers?.map((carrier, carrierIndex) => (
                <EcheckCarrier
                    hasAllowedToSetCustomCarrierShipperPermission={props.hasAllowedToSetCustomCarrierShipperPermission}
                    partOfBundle
                    dragId={`bundle-${props.carrierIndex}-carrier-${carrierIndex}`}

                    playSuccess={props.playSuccess}
                    playError={props.playError}
                    maxCarrierAmount={props.maxCarrierAmount}
                    activeDragItem={props.activeDragItem}
                    carrierToAddTo={props.carrierToAddTo}
                    setCarrierToAddTo={props.setCarrierToAddTo}
                    bundleIndex={props.carrierIndex}
                    setCarriers={props.setCarriers}
                    key={"containerview"+carrierIndex}
                    deliveryOptions={props.deliveryOptions}
                    selectLabel={props.selectLabel}
                    selectManualCarrier={props.selectManualCarrier}
                    OwnCarrierClick={props.OwnCarrierClick}
                    carriersSelect={props.carriersSelect}
                    carrier={carrier}
                    carrierIndex={carrierIndex}
                    isBundling={props.isBundling}
                    toScanRows={props.toScanRows}
                    setToScanRows={props.setToScanRows}
                    setOriginalOrderlines={props.setOriginalOrderlines}
                    setSkuSerialMap={props.setSkuSerialMap}
                    setHighlightColor={props.setHighlightColor}

                    autoFinalize={props.autoFinalize}
                    carrierMandatory={props.carrierMandatory}
                    selectedShipper={props.selectedShipper}
                    selectedShipperProfile={props.selectedShipperProfile}
                    setGoFinalize={props.setGoFinalize}
                    shippers={props.shippers}
                    currentOrder={props.currentOrder}
                    clearInstructionShown={props.clearInstructionShown}
                />
            ))}

            <MDynamicValues
                open={updateQuarantineModal.open}
                handleClose={() => updateQuarantineModal.setOpen(false)}
                title={t("edit_carrier_dimensions")}
                labelWidth={144}
                fields={[
                    { name: "length", label: t("length"), type: "integer", defaultValue: props.carrier?.dimensions?.length ?? 0},
                    { name: "width", label: t("width"), type: "integer", defaultValue: props.carrier?.dimensions?.width ?? 0},
                    { name: "height", label: t("height"), type: "integer", defaultValue: props.carrier?.dimensions?.height ?? 0}
                ]}
                onConfirm={setCarrierDimensions}
            />

            <MEcheckCarrierShipper
                open={shipperOverrideModalOpen}
                handleClose={() => setShipperOverrideModalOpen(false)}
                selectedShipper={props.selectedShipper}
                currentShipperProfileId={props.carrier?.shipperProfileId}
                onConfirm={applyShipperOverride}
                onClear={clearShipperOverride}
            />
        </Box>
    );
}


export const EcheckCarrierReadOnly = (props) => {
        
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 

    const [getShipmentLabel, { data: shipmentLabelData, isLoading: shipmentLabelIsLoading, isError: shipmentLabelIsError }] = useGetShipmentLabelMutationMutation();

    const [printShipmentLabel, { isLoading: printShipmentLabelIsLoading }] = usePrintShipmentLabelMutation();
    const selectedWorkSpace = useSelector((state) => state.settings.workSpace);

    const [getPickingSlip, { data: pickingSlipData, isLoading: pickingSlipLoading, isError: pickingSlipIsError }] = useGetPickingSlipMutation();

    const [labelsModal, setLabelsModal] = useState({ open: false, labels: [], type: "" });

    const handlePrintLabel = async () => {
        if (printShipmentLabelIsLoading) return;
        await printShipmentLabel({
            shipmentId: props.shipmentIds[props.carrierIndex],
            workspaceId: selectedWorkSpace?.id
        }).unwrap().then(() => {
            showToastMessage("success", t("label_sent_to_printer"));
        }).catch(err => {
            showToastMessage("error", t("label_print_failed"));
        });
    };


    return (
        <Box
            sx={{
                background: theme.palette.mode === "dark" ? colors.grey[500] : colors.bg["tertiary"],
                borderRadius: 4,
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                paddingBottom: 3,
                postion:"relative",
                px:2,
                py:2,
                "&.active":{
                    background:theme.palette.mode==="dark" ? colors.blue[100] : colors.primary[100],
                    border:`1px solid ${theme.palette.mode==="dark" ? colors.blue[400] : colors.primary[400]}`,
                }
            }}
        >
            <Box
                sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    alignItems:'flex-start'
                }}
            >
                <Box
                    sx={{
                        pb:2,
                    }}
                >
                    <Text semibold>
                        {props.carrier.carrierName === '' ? "Scan drager ..." : props.carrier.carrierName
                        }
                    </Text>
                    <Box pt={0.5}>
                        <Text bold>{props.carrier.totalProducts}</Text> <Text light>Producten,</Text>&nbsp;
                        <Text bold>{formatNumber(i18n,props.carrier.totalWeight/1000)}</Text> <Text light>kg</Text>

                    </Box>
                </Box>
                <Box
                    sx={{
                        display:"flex",
                        gap:1,
                    }}
                >
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                        isLoading={printShipmentLabelIsLoading}
                        onClick={handlePrintLabel}
                        startIcon={<Print/>}
                        variant="contained"
                    >
                        {t("label")}
                    </Shbutton>
                    <Shbutton
                        color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                        isLoading={shipmentLabelIsLoading}
                        onClick={async () => {
                            if(!shipmentLabelIsLoading){
                                await getShipmentLabel(props.shipmentIds[props.carrierIndex]).unwrap().then(res => {
                                    if (res.labels?.length > 1) {
                                        // If multiple labels, open modal with buttons
                                        setLabelsModal({
                                            open: true,
                                            labels: res.labels,
                                            type: res.type
                                        });
                                    } else if (res.labels?.length === 1) {
                                        // If single label, open directly
                                        const label = res.labels[0];
                                        if (res.type === "pdf") {
                                            const decodedData = atob(label);
                                            const arrayBuffer = new ArrayBuffer(decodedData?.length);
                                            const uint8Array = new Uint8Array(arrayBuffer);
                                            for (let i = 0; i < decodedData.length; i++) {
                                                uint8Array[i] = decodedData.charCodeAt(i);
                                            }
                                            const blob = new Blob([uint8Array], { type: 'application/pdf' });
                                            const pdfUrl = URL.createObjectURL(blob);
                                            window.open(pdfUrl, '_blank');
                                        } else if (res.type === "link") {
                                            window.open(label, '_blank');
                                        }
                                    }
                                }).catch(err => {
                                    // console.error(err);
                                })
                            }
                        }}
                        startIcon={<Download/>}
                        variant="contained"
                        sx={{
                            background:"#C5882F",
                            color:"#fff"
                        }}
                    >
                        {t("label")}
                    </Shbutton>
                    {props.hasPickingslip &&
                        <Shbutton
                            color={theme.palette.mode === "dark" ? "primaryGrey" : "grey"}
                            isLoading={pickingSlipLoading}
                            onClick={async () => {
                                if(!pickingSlipLoading){
                                    const response = await getPickingSlip(props.shipmentIds[props.carrierIndex]).unwrap();
                                    // Decode base64 PDF from response.data and create blob URL
                                    const base64Data = response.data;
                                    const decodedData = atob(base64Data);
                                    const arrayBuffer = new ArrayBuffer(decodedData.length);
                                    const uint8Array = new Uint8Array(arrayBuffer);
                                    for (let i = 0; i < decodedData.length; i++) {
                                        uint8Array[i] = decodedData.charCodeAt(i);
                                    }
                                    const blob = new Blob([uint8Array], { type: 'application/pdf' });
                                    const pdfUrl = URL.createObjectURL(blob);
                                    window.open(pdfUrl, '_blank');
                                }
                            }}
                            startIcon={<TurnedInIcon/>}
                            variant="contained"
                        >
                            {t("pickingslip")}
                        </Shbutton>
                    }
                </Box>
            </Box>
            <Box
                sx={{
                    display:'flex',
                    flexDirection:'column',
                    gap:1.5,
                }}
            >
                {props.carrier.rows.map((row, index) => {

                    return (
                        <CarrierRowReadOnly 
                            key={index}
                            row={row}
                            carrierIndex={props.carrierIndex}
                            bundleIndex={props.bundleIndex}
                            rowIndex={index}
                        />
                    )
                })}
               
            </Box>
            
             <MSelectLabel
                open={labelsModal.open}
                handleClose={() => setLabelsModal({ ...labelsModal, open: false })}
                labels={labelsModal.labels}
                type={labelsModal.type}
            />
        </Box>
    );
}

export const EcheckCarrierBundleReadonly = (props) => {
        
    const { t, ready,i18n } = useTranslation();
    const theme = useTheme();
    const colors = tokens(theme.palette.mode); 

    return (
        <Box
            key={props.carrierIndex}
            sx={{
                background:theme.palette.mode === "dark" ? colors.grey[300] : colors.grey[50],
                border: `1px solid  ${theme.palette.mode === "dark" ? colors.grey[800] : colors.grey[200]}`,
                p:2,
                borderRadius:6,
                display:'flex',
                gap:1.5,
                flexDirection:'column',
            }}
        >
            <Box
                sx={{
                    display:"flex",
                    justifyContent:"space-between",
                    alignItems:'flex-start'
                }}
            >
                <Box>
                    <Text semibold>
                        {props.carrier.carrierName === '' ? "Scan drager ..." : props.carrier.carrierName }
                    </Text>
                    <Box>
                        <Text bold>{props.carrier.totalProducts}</Text> <Text light>Producten,</Text>&nbsp;
                        <Text bold>{formatNumber(i18n,props.carrier.totalWeight/1000)}</Text> <Text light>kg</Text>
                    </Box>
                </Box>
            </Box>
            {props.carrier.carriers && props.carrier.carriers?.map((carrier, carrierIndex) => (
                <EcheckCarrierReadOnly
                    partOfBundle
                    shipmentIds={props.shipmentIds}
                    key={"containerview"+carrierIndex}
                    carrier={carrier}
                    carrierIndex={carrierIndex}
                    hasPickingslip={props.hasPickingslip}
                    readOnly
                />
            ))}
        </Box>
    );
}